import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, AlertTriangle } from "lucide-react";
import { seoHead, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";

const SECTIONS: { title: string; checks: string[] }[] = [
  {
    title: "Lights & visibility",
    checks: [
      "All headlight bulbs working (dipped, main beam, indicators, brake lights)",
      "No cracked or misaligned light units",
      "Number plate lights working",
      "Front and rear fog lights functional",
      "Wipers don't streak; washer fluid topped up",
      "Windscreen free of cracks larger than 10mm in the driver's view",
    ],
  },
  {
    title: "Tyres & wheels",
    checks: [
      "All four tyres above 1.6mm tread depth across the central 3/4",
      "No cuts, bulges or sidewall damage",
      "Tyre pressures correct (check the pillar sticker)",
      "Spare tyre or repair kit present (if originally supplied)",
      "No missing wheel nuts; locking wheel nut key in the car",
    ],
  },
  {
    title: "Brakes & suspension",
    checks: [
      "No squealing, grinding or pulling under braking",
      "Handbrake holds the car firmly on a slope",
      "No knocking from the suspension over bumps",
      "Steering is straight with no excessive play",
    ],
  },
  {
    title: "Inside the car",
    checks: [
      "All seatbelts retract and lock cleanly",
      "Horn works",
      "Mirrors all present and intact",
      "Driver and front-passenger airbag warning lights off",
      "Front seats move and lock as designed",
    ],
  },
  {
    title: "Under the bonnet",
    checks: [
      "Engine oil between min and max",
      "Coolant and brake fluid topped up",
      "No obvious fluid leaks under the car",
      "Battery terminals secure",
    ],
  },
  {
    title: "Paperwork & basics",
    checks: [
      "V5C logbook to hand (you don't strictly need it but it speeds re-registration if anything changes)",
      "Mileage roughly known (we'll record exact)",
      "Locking wheel-nut key in the car",
      "Boot empty enough to inspect the floor and spare-wheel well",
    ],
  },
];

const MOT_FAQS = [
  {
    q: "What's the most common MOT failure?",
    a: "In the UK the top failures every year are lights and signalling, suspension, brakes and tyres. Most are cheap to fix in advance — bulbs, wiper blades and tyre pressures alone account for thousands of failures.",
  },
  {
    q: "Will a dashboard warning light fail my MOT?",
    a: "Yes, in most cases. Engine management (EML), ABS, airbag, ESC and brake-system warnings are an automatic fail. Get them diagnosed before booking the test — we charge £45 and the fee comes off any repair.",
  },
  {
    q: "Can I drive my car if it fails the MOT?",
    a: "Only if the previous certificate is still valid AND there are no 'dangerous' defects. If your old MOT has expired, the only legal journey is to a pre-arranged appointment to fix the failure or a re-test.",
  },
  {
    q: "Do I need to bring the V5C logbook?",
    a: "No — the test station looks the vehicle up by registration. But it's good practice to have it in case any details need verifying.",
  },
  {
    q: "How early can I book my MOT?",
    a: "Up to 28 days before the current certificate expires without losing any days. The new certificate runs from the old expiry date.",
  },
  {
    q: "Do you charge for re-tests?",
    a: "If the repair is done by us within the DVSA-allowed 10-working-day window, the partial re-test is free.",
  },
];

export const Route = createFileRoute("/mot-checklist")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/mot-checklist",
      title: "Free MOT Checklist 2026 — Pass First Time | Woodseer E14",
      description:
        "Free 30-point MOT pre-check from a DVSA-authorised test centre. Lights, tyres, brakes, dashboard warnings, paperwork — exactly what to check before you book your MOT in East London.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "MOT Checklist", path: "/mot-checklist" },
      ],
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
        faqSchema(MOT_FAQS) as Record<string, unknown>,
        {
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to pass your MOT first time",
          description: "Six checks any driver can do before booking an MOT to dramatically improve the chance of a first-time pass.",
          step: SECTIONS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.title,
            text: s.checks.join(" "),
          })),
        } as Record<string, unknown>,
      ],
    }),
  component: MotChecklistPage,
});

function MotChecklistPage() {
  return (
    <>
      <section className="py-14 md:py-20 bg-secondary">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">Free Pre-MOT Checklist</p>
          <h1 className="mt-3 text-4xl md:text-5xl font-extrabold text-foreground tracking-tight">Pass your MOT first time — 30-point check</h1>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed">
            Spend 15 minutes on these six checks before your MOT. They're the same things our DVSA-authorised testers look at first — and they're the reason most local cars fail.
          </p>
          <div className="mt-6 inline-flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            If a dashboard warning light is on, get it diagnosed first — it's an automatic fail in most cases.
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-4xl px-4 grid sm:grid-cols-2 gap-5">
          {SECTIONS.map((s) => (
            <div key={s.title} className="rounded-2xl border border-border bg-card p-5">
              <h2 className="text-base font-bold text-foreground">{s.title}</h2>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                {s.checks.map((c) => (
                  <li key={c} className="flex items-start gap-2"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{c}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mx-auto max-w-2xl px-4 mt-10 text-center">
          <p className="text-sm text-muted-foreground">Anything on the list looking iffy? Bring it in for a free 5-minute pre-check before you book the test — way cheaper than paying for a fail.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Link to="/book" search={{ service: "mot" }} className="rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground">Book MOT in East London →</Link>
            <Link to="/mot-checklist-before-test" className="rounded-lg border border-border px-5 py-3 text-sm font-bold text-foreground">Full pre-MOT guide</Link>
            <Link to="/pricing" className="rounded-lg border border-border px-5 py-3 text-sm font-bold text-foreground">See pricing</Link>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={MOT_FAQS} title="MOT preparation FAQs" />
    </>
  );
}
